import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatMoney, formatNumber, sourceLabel, timeFromNow, formatDate } from "@/lib/api";
import { SourcePill, StatusTag } from "@/components/Tags";
import { ArrowLeft, ExternalLink, Trash2, RotateCcw, Save, Calculator } from "lucide-react";

export default function LotDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [ownerInput, setOwnerInput] = useState({ name: "", state: "", notes: "" });
  const [owners, setOwners] = useState([]);
  const [savingOwner, setSavingOwner] = useState(false);

  async function load() {
    const [d, ow] = await Promise.all([api.get(`/lots/${id}`), api.get(`/owners`)]);
    setData(d.data);
    setOwners(ow.data);
    setOwnerInput({
      name: d.data.owner?.name || "",
      state: d.data.owner?.state || "",
      notes: d.data.owner?.notes || "",
    });
  }
  useEffect(() => { load();   }, [id]);

  if (!data) return <div className="text-[color:var(--caa-muted)]">Loading…</div>;
  const { lot, sightings } = data;

  async function saveOwner() {
    if (!ownerInput.name.trim()) return;
    setSavingOwner(true);
    try {
      const existing = owners.find(
        (o) => o.name.trim().toLowerCase() === ownerInput.name.trim().toLowerCase()
      );
      let ownerId = existing?.id;
      if (!ownerId) {
        const r = await api.post("/owners", ownerInput);
        ownerId = r.data.id;
      }
      await api.patch(`/lots/${id}`, { owner_id: ownerId });
      await load();
    } finally {
      setSavingOwner(false);
    }
  }

  async function toggleDismiss() {
    if (lot.dismissed) await api.post(`/lots/${id}/restore`);
    else await api.post(`/lots/${id}/dismiss`);
    await load();
  }

  return (
    <div className="space-y-6" data-testid="lot-detail">
      <button onClick={() => nav(-1)}
        className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--caa-muted)] hover:text-[color:var(--caa-charcoal)]"
        data-testid="back-btn">
        <ArrowLeft size={15} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="caa-card p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <SourcePill source={lot.source} />
                  <StatusTag status={lot.status} />
                  {lot.dismissed && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[color:var(--caa-status-passed-bg)] text-[color:var(--caa-status-passed)]">
                      Dismissed
                    </span>
                  )}
                </div>
                <h1 className="text-2xl font-extrabold text-[color:var(--caa-charcoal)] leading-tight">{lot.title}</h1>
                <p className="text-sm text-[color:var(--caa-muted)] mt-1">
                  Lot {lot.lot_number || "—"} · {lot.location || "—"} · {lot.state || "—"}
                </p>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-[color:var(--caa-muted)] font-semibold">
                  {lot.status === "sold" ? "Sold for" : "Current bid"}
                </div>
                <div className="text-3xl font-extrabold caa-num" style={{ color: "var(--caa-teal-deep)" }}>
                  {formatMoney(lot.final_bid || lot.current_bid)}
                </div>
                <div className="text-xs text-[color:var(--caa-muted)] mt-1">
                  {lot.bid_count || 0} bid{lot.bid_count === 1 ? "" : "s"}
                  {lot.status === "open" && lot.closing_at ? ` · closes ${timeFromNow(lot.closing_at)}` : ""}
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Make" value={lot.make} />
              <Field label="Model" value={lot.model_name} />
              <Field label="Year" value={lot.year} />
              <Field label="Serial" value={lot.serial} />
              <Field label="Class" value={lot.klass} />
              <Field label="Capacity" value={lot.capacity_t ? `${lot.capacity_t}T` : "—"} />
              <Field label="Hours" value={formatNumber(lot.hours)} />
              <Field label="Kilometres" value={formatNumber(lot.km)} />
              <Field label="Starting bid" value={formatMoney(lot.starting_bid)} />
            </div>

            {lot.description && (
              <div className="mt-6 pt-4 border-t caa-hairline">
                <div className="text-xs uppercase tracking-wider text-[color:var(--caa-muted)] font-semibold mb-2">Description</div>
                <p className="text-sm text-[color:var(--caa-charcoal)] whitespace-pre-line">{lot.description}</p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              {lot.url && (
                <a href={lot.url} target="_blank" rel="noopener noreferrer" data-testid="open-original"
                  className="inline-flex items-center gap-2 rounded-lg border caa-hairline px-3 py-2 text-sm font-semibold text-[color:var(--caa-charcoal)] hover:bg-[color:var(--caa-teal-soft)] hover:text-[color:var(--caa-teal-deep)]">
                  <ExternalLink size={14} /> Open original listing
                </a>
              )}
              <Link to={`/valuation?make=${encodeURIComponent(lot.make || "")}&model=${encodeURIComponent(lot.model_name || "")}&year=${lot.year || ""}&hours=${lot.hours || ""}&klass=${encodeURIComponent(lot.klass || "")}`}
                data-testid="value-crane"
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white"
                style={{ background: "var(--caa-teal-deep)" }}>
                <Calculator size={14} /> Value this crane
              </Link>
              <button onClick={toggleDismiss} data-testid="dismiss-btn"
                className="inline-flex items-center gap-2 rounded-lg border caa-hairline px-3 py-2 text-sm font-semibold text-[color:var(--caa-charcoal)] hover:bg-[color:var(--caa-teal-soft)]">
                {lot.dismissed ? <><RotateCcw size={14} /> Restore</> : <><Trash2 size={14} /> Dismiss</>}
              </button>
            </div>
          </div>

          <div className="caa-card p-6" data-testid="bid-history-card">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <h3 className="font-bold text-[color:var(--caa-charcoal)]">Bid history</h3>
                <p className="text-xs text-[color:var(--caa-muted)] mt-1">
                  Bid changes we&apos;ve observed. Bidder handles are shown when the source lists them (masked pseudonyms only).
                </p>
              </div>
              <BidSparkline history={lot.bid_history} currentBid={lot.current_bid} />
            </div>
            {(!lot.bid_history || lot.bid_history.length === 0) ? (
              <p className="text-sm text-[color:var(--caa-muted)]" data-testid="bid-history-empty">
                No bid changes captured yet. Amounts will appear here as they change.
              </p>
            ) : (
              <ul className="divide-y caa-hairline" data-testid="bid-history-list">
                {[...lot.bid_history].reverse().map((h, i) => (
                  <li key={`${h.at}-${i}`} className="py-3 flex items-center justify-between gap-3" data-testid="bid-history-item">
                    <div>
                      <div className="font-bold caa-num" style={{ color: "var(--caa-teal-deep)" }}>
                        {formatMoney(h.amount)}
                      </div>
                      <div className="text-xs text-[color:var(--caa-muted)]">
                        {formatDate(h.at)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-wider font-semibold text-[color:var(--caa-muted)]">Bidder</div>
                      <div className="text-sm font-semibold text-[color:var(--caa-charcoal)]" data-testid="bid-history-handle">
                        {h.bidder_handle || "—"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="caa-card p-6" data-testid="sightings-card">
            <h3 className="font-bold text-[color:var(--caa-charcoal)]">Serial-matched history</h3>
            <p className="text-xs text-[color:var(--caa-muted)] mt-1 mb-4">
              Other sales of the same serial number ({lot.serial || "unknown"}).
            </p>
            {sightings.length === 0 ? (
              <p className="text-sm text-[color:var(--caa-muted)]">No previous sightings for this serial.</p>
            ) : (
              <ul className="divide-y caa-hairline">
                {sightings.map((s) => (
                  <li key={s.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-[color:var(--caa-charcoal)]">
                        {sourceLabel(s.source)} · {formatDate(s.date)}
                      </div>
                      <div className="text-xs text-[color:var(--caa-muted)] capitalize">{s.outcome}</div>
                    </div>
                    <div className="font-bold caa-num" style={{ color: "var(--caa-teal-deep)" }}>
                      {formatMoney(s.price)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="caa-card p-6" data-testid="owner-card">
            <h3 className="font-bold text-[color:var(--caa-charcoal)]">Owner</h3>
            <p className="text-xs text-[color:var(--caa-muted)] mt-1 mb-4">
              Attach an owner record to this machine (creates one if it doesn&apos;t exist).
            </p>
            <div className="space-y-3">
              <OwnerField label="Name" testId="owner-name" value={ownerInput.name}
                onChange={(v) => setOwnerInput((s) => ({ ...s, name: v }))}
                suggestions={owners.map((o) => o.name)} />
              <OwnerField label="State" testId="owner-state" value={ownerInput.state}
                onChange={(v) => setOwnerInput((s) => ({ ...s, state: v }))} />
              <OwnerField label="Notes" testId="owner-notes" value={ownerInput.notes}
                onChange={(v) => setOwnerInput((s) => ({ ...s, notes: v }))} />
              <button onClick={saveOwner} disabled={savingOwner || !ownerInput.name.trim()}
                data-testid="owner-save"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "var(--caa-teal-deep)" }}>
                <Save size={14} /> {savingOwner ? "Saving…" : "Save owner"}
              </button>
            </div>
          </div>

          <div className="caa-card p-6">
            <h3 className="font-bold text-[color:var(--caa-charcoal)]">Auction summary</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <SummaryRow label="Source" value={sourceLabel(lot.source)} />
              <SummaryRow label="Lot ID" value={lot.source_lot_id || "—"} />
              <SummaryRow label="First seen" value={formatDate(lot.first_seen)} />
              <SummaryRow label="Last seen" value={formatDate(lot.last_seen)} />
              <SummaryRow label="Closes"
                value={lot.closing_at ? `${formatDate(lot.closing_at)} · ${timeFromNow(lot.closing_at)}` : "—"} />
              {lot.final_bid ? <SummaryRow label="Final" value={formatMoney(lot.final_bid)} /> : null}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-[color:var(--caa-muted)]">{label}</div>
      <div className="text-sm font-semibold text-[color:var(--caa-charcoal)] mt-0.5">{value || "—"}</div>
    </div>
  );
}

function OwnerField({ label, value, onChange, testId, suggestions }) {
  const listId = testId + "-list";
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[color:var(--caa-muted)]">{label}</label>
      <input data-testid={testId} value={value} list={suggestions ? listId : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border caa-hairline bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--caa-teal-deep)]" />
      {suggestions && (
        <datalist id={listId}>
          {suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-[color:var(--caa-muted)]">{label}</span>
      <span className="font-semibold text-[color:var(--caa-charcoal)] text-right">{value}</span>
    </li>
  );
}


function BidSparkline({ history, currentBid }) {
  // Need ≥2 real bid points to draw a trajectory. Blank beats a fake line.
  const points = (history || []).filter((h) => h && typeof h.amount === "number");
  if (points.length < 2) {
    return (
      <div className="text-right shrink-0" data-testid="bid-sparkline-empty">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--caa-muted)]">
          Current bid
        </div>
        <div className="font-bold caa-num text-lg" style={{ color: "var(--caa-teal-deep)" }}>
          {typeof currentBid === "number"
            ? `$${currentBid.toLocaleString("en-AU")}`
            : "—"}
        </div>
      </div>
    );
  }

  // Sort ascending by time
  const sorted = [...points].sort((a, b) => new Date(a.at) - new Date(b.at));
  const amounts = sorted.map((p) => p.amount);
  const times = sorted.map((p) => new Date(p.at).getTime());
  const minA = Math.min(...amounts);
  const maxA = Math.max(...amounts);
  const minT = times[0];
  const maxT = times[times.length - 1];
  const W = 180;
  const H = 44;
  const Hpad = 6;

  const x = (t) => (maxT === minT ? W / 2 : ((t - minT) / (maxT - minT)) * (W - 8) + 4);
  const y = (a) => {
    if (maxA === minA) return H / 2;
    return H - Hpad - ((a - minA) / (maxA - minA)) * (H - Hpad * 2);
  };

  const d = sorted.map((p, i) => `${i === 0 ? "M" : "L"} ${x(times[i]).toFixed(1)} ${y(p.amount).toFixed(1)}`).join(" ");
  const areaD = `${d} L ${W - 4} ${H - Hpad} L 4 ${H - Hpad} Z`;

  const last = sorted[sorted.length - 1];
  const first = sorted[0];
  const delta = last.amount - first.amount;
  const climbing = delta > 0;

  return (
    <div className="text-right shrink-0" data-testid="bid-sparkline">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--caa-muted)]">
        {points.length} observed bids
      </div>
      <svg width={W} height={H} className="block ml-auto" aria-label="Bid trajectory">
        <defs>
          <linearGradient id="caa-spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--caa-teal-deep)" stopOpacity="0.28" />
            <stop offset="1" stopColor="var(--caa-teal-deep)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#caa-spark-fill)" />
        <path d={d} fill="none" stroke="var(--caa-teal-deep)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {sorted.map((p, i) => (
          <circle key={i} cx={x(times[i])} cy={y(p.amount)} r={i === sorted.length - 1 ? 3.5 : 2} fill="var(--caa-teal-deep)" />
        ))}
      </svg>
      <div className="text-xs mt-0.5 caa-num" style={{ color: climbing ? "var(--caa-status-live)" : "var(--caa-muted)" }}>
        {climbing ? "▲" : "▼"} ${Math.abs(delta).toLocaleString("en-AU")} since first
      </div>
    </div>
  );
}
